import React, { useCallback, useContext, useState } from 'react';
import { Navigate } from 'react-router-dom';
import useQueryParams from 'hooks/useQueryParams';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import useValidations from '../../hooks/useValidations';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import auth from 'api/auth';
import { Button } from 'ncoded-component-library';
import PasswordField from 'components/PasswordField';
import { FORGOT_PASSWORD_ROUTE } from 'data/global';
import AuthContext from 'providers/Auth/Auth.context';
import { getShareableLinkRoute } from 'utils';

import './ResetPassword.styles.scss';

type ResetPasswordProps = {
  className?: string;
};

const ResetPassword: React.FC<ResetPasswordProps> = () => {
  const {
    params: { token, shareableLinkId },
  } = useQueryParams<{ token: string; shareableLinkId?: string }>();

  const [loading, setLoading] = useState(false);

  const { popServerError } = useContext(PopNotificationsContext);
  const { onLoginReq } = useContext(AuthContext);

  const validations = useValidations();

  const { t } = useTranslation();

  const onSubmit = useCallback(
    async ({ newPassword }: { newPassword: string }) => {
      try {
        setLoading(true);
        const { data } = await auth.resetPassword(token, newPassword);
        onLoginReq(
          data,
          shareableLinkId ? getShareableLinkRoute(shareableLinkId) : undefined,
        );
      } catch (er) {
        popServerError(er);
      } finally {
        setLoading(false);
      }
    },
    [onLoginReq, popServerError, shareableLinkId, token],
  );

  if (!token) return <Navigate to={FORGOT_PASSWORD_ROUTE} replace />;

  return (
    <>
      <h1>{t('resetPassword')}</h1>
      <Form
        onSubmit={onSubmit}
        render={({ handleSubmit }) => (
          <form className="auth-login" onSubmit={handleSubmit}>
            <Field
              placeholder={t('password')}
              label={t('password')}
              required
              name="newPassword"
              component={PasswordField}
              validate={validations.password}
            />
            <div className="actions">
              <Button type="submit" disabled={loading} data-loading={loading}>
                {t('resetPassword')}
              </Button>
            </div>
          </form>
        )}
      />
    </>
  );
};

export default ResetPassword;
